// «Convertir en canal privado» (shared/API.md §32; port of GroupMessageConversionSheet.swift /
// Android's). The group has no team: the server names the teams every participant shares, the
// reader's role grants narrow that to teams where a private channel can be created — a form that
// can only end in 403 is never offered. Conversion keeps the id, history and participants.
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { data } from '../../../bridge/client';
import { CreateChannelForm } from '../../../core/createChannelForm';
import { GroupMessageConversionForm, type Failure } from '../../../core/groupMessageConversionForm';
import type { ConversionTeam } from '../../../data/features/convertGroup';
import { t } from '../../../i18n/i18n';
import { Button, Dialog } from '../../kit/Kit';
import { useShell } from '../../shell/ShellContext';
import '../people/people.css';

function failureText(f: Failure): string {
  return f === 'nameTaken' ? t('createChannel.failedTaken') : f === 'notPermitted' ? t('createChannel.failedPermission') : t('createChannel.failedOther');
}

export function ConvertGroupDialog({ channelId, groupTitle, onClose, onConverted }: { channelId: string; groupTitle: string; onClose(): void; onConverted(): void }) {
  const shell = useShell();
  const [teams, setTeams] = useState<ConversionTeam[] | null>(null);
  const [teamId, setTeamId] = useState<string | null>(null);
  const [displayName, setDisplayName] = useState(() => GroupMessageConversionForm.initialDisplayName(groupTitle));
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<Failure | null>(null);

  useEffect(() => {
    let alive = true;
    void data.call('groupConversionTeams', channelId).then(
      (r) => {
        if (!alive) return;
        const list = 'teams' in r ? r.teams : [];
        setTeams(list);
        setTeamId(GroupMessageConversionForm.initialTeamId(list, shell.team.id));
      },
      () => alive && setTeams([]),
    );
    return () => {
      alive = false;
    };
  }, [channelId, shell.team.id]);

  const submittable = GroupMessageConversionForm.isSubmittable(teamId, displayName) && !busy;
  const convert = async () => {
    if (!submittable || !teamId) return;
    setBusy(true);
    setFailure(null);
    const r = await data.call('convertGroupToChannel', channelId, teamId, displayName).catch(() => ({ ok: false as const, failure: 'other' as const }));
    setBusy(false);
    if (r.ok) onConverted();
    else setFailure(r.failure);
  };

  // Portaled: opened from the side panel, whose backdrop-filter would otherwise become the fixed
  // scrim's containing block (the dialog drew inside the panel, off screen).
  return createPortal(
    <Dialog
      title={t('groupConversion.title')}
      onClose={onClose}
      width={460}
      footer={
        teams && teams.length ? (
          <>
            <span className={`people-footer-hint${failure ? ' error' : ''}`} role={failure ? 'alert' : undefined}>
              {failure ? failureText(failure) : ''}
            </span>
            <Button kind="primary" onClick={() => void convert()} disabled={!submittable}>
              {t('groupConversion.convert')}
            </Button>
          </>
        ) : undefined
      }
    >
      {teams == null ? (
        <p className="people-field-hint">{t('channel.loading.accessibility')}…</p>
      ) : teams.length === 0 ? (
        <p className="people-field-hint">{t('groupConversion.noTeams')}</p>
      ) : (
        <div className="people-form">
          <label className="people-field">
            <span className="people-field-label">{t('createChannel.name')}</span>
            <input
              className="people-input"
              value={displayName}
              maxLength={CreateChannelForm.maxDisplayName}
              autoFocus
              onChange={(e) => {
                setDisplayName(e.target.value);
                if (failure === 'nameTaken') setFailure(null);
              }}
              onKeyDown={(e) => e.key === 'Enter' && !e.nativeEvent.isComposing && (e.preventDefault(), void convert())}
            />
          </label>
          {teams.length > 1 ? (
            <label className="people-field">
              <span className="people-field-label">{t('groupConversion.team')}</span>
              <select className="people-input" value={teamId ?? ''} onChange={(e) => setTeamId(e.target.value)}>
                {teams.map((tm) => (
                  <option key={tm.id} value={tm.id}>
                    {tm.displayName}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          <span className="people-field-hint">{t('groupConversion.hint')}</span>
        </div>
      )}
    </Dialog>,
    document.body,
  );
}
